import React, { useRef, useEffect, useMemo, useCallback } from 'react';
import dayjs from 'dayjs';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import TimeGrid from './TimeGrid';
import TrainingEvent from './TrainingEvent';
import { useCalendarView } from '../../../hooks/calendar/useCalendarView';
import { useTimeSlotDrag } from '../../../hooks/calendar/useTimeSlotDrag';
import { calculateEventPositions, filterEventsByDateRange } from '../../../utils/calendar/calendarHelpers';
import {
    getDayBounds,
    pixelsToTime,
    timeToPixels,
    getNonWorkingBlocks,
    snapToGrid
} from '../../../utils/calendar/timeCalculations';

dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);

const dayNamesPl = ["Pon", "Wt", "Śr", "Czw", "Pt", "Sob", "Nie"];

const CalendarGrid = ({
                          selectedTrainer,
                          workingHours,
                          events,
                          currentWeekStart,
                          onEventClick,
                          onEventEdit,
                          onEventDelete,
                          onTimeSlotClick,
                          onEventMove,
                          onEventResize,
                          isLockMode = false,
                          selectedLockSlots = [],
                          setSelectedLockSlots,
                          onEditTemporaryLock,
                          onDeleteTemporaryLock,
                      }) => {
    const containerRef = useRef(null);

    // Use calendar view hook with currentWeekStart
    const {
        timeRange,
        gridDimensions,
        daysOfWeek,
        scrollToCurrentTime,
        currentTimePosition,
    } = useCalendarView(workingHours, currentWeekStart);

    // Memoize day start calculation
    const dayStart = useMemo(() => {
        return currentWeekStart.hour(parseInt(timeRange.earliestTime.split(':')[0])).minute(0).second(0);
    }, [currentWeekStart, timeRange.earliestTime]);

    // Use drag hook for normal event operations (confirmed trainings only)
    const {
        handleDragStart,
        handleResizeStart,
        handleSelectionStart,
        handleDragMove,
        handleSelectionMove,
        handleDragEnd,
        handleSelectionEnd,
        ghostPosition,
        selectionBox,
    } = useTimeSlotDrag({
        onEventMove,
        onEventResize,
        onTimeRangeSelect: isLockMode ? null : onTimeSlotClick,
        existingEvents: events,
        dayStart,
        hourHeight: gridDimensions.hourHeight,
    });

    // Scroll to current time on mount
    useEffect(() => {
        scrollToCurrentTime(containerRef);
    }, [scrollToCurrentTime]);

    // Handle mouse move and up for normal event dragging (not lock mode)
    const handleMouseMove = useCallback((e) => {
        if (!isLockMode) {
            handleDragMove(e);
            handleSelectionMove(e);
        }
    }, [isLockMode, handleDragMove, handleSelectionMove]);

    const handleMouseUp = useCallback((e) => {
        if (!isLockMode) {
            handleDragEnd();
            handleSelectionEnd();
        }
    }, [isLockMode, handleDragEnd, handleSelectionEnd]);

    // Add global mouse event listeners for normal drag (not lock mode)
    useEffect(() => {
        if (!isLockMode) {
            document.addEventListener('mousemove', handleMouseMove);
            document.addEventListener('mouseup', handleMouseUp);
            document.addEventListener('touchmove', handleMouseMove);
            document.addEventListener('touchend', handleMouseUp);

            return () => {
                document.removeEventListener('mousemove', handleMouseMove);
                document.removeEventListener('mouseup', handleMouseUp);
                document.removeEventListener('touchmove', handleMouseMove);
                document.removeEventListener('touchend', handleMouseUp);
            };
        }
    }, [isLockMode, handleMouseMove, handleMouseUp]);

    // Handle empty slot click - universal for both normal and lock modes
    const handleEmptySlotClick = useCallback((e, dayDate) => {
        if (!onTimeSlotClick) return;

        const rect = e.currentTarget.getBoundingClientRect();
        const y = e.clientY - rect.top;

        const dayStartTime = dayDate.hour(parseInt(timeRange.earliestTime.split(':')[0])).minute(0).second(0);
        const rawClickTime = pixelsToTime(y, dayStartTime, gridDimensions.hourHeight);

        // Snap to 15-minute intervals
        const snappedClickTime = snapToGrid(rawClickTime, 15);

        if (isLockMode) {
            // For lock mode, just call onTimeSlotClick with the snapped time
            onTimeSlotClick(snappedClickTime);
        } else {
            // For normal mode, use the existing selection start handler
            handleSelectionStart(e, snappedClickTime);
        }
    }, [onTimeSlotClick, isLockMode, gridDimensions.hourHeight, handleSelectionStart, timeRange.earliestTime]);

    // Memoized event handlers
    const memoizedEventHandlers = useMemo(() => ({
        onEdit: onEventEdit,
        onDelete: onEventDelete,
        onClick: onEventClick,
        onDragStart: handleDragStart,
        onResizeStart: handleResizeStart,
    }), [onEventEdit, onEventDelete, onEventClick, handleDragStart, handleResizeStart]);

    // Render day column content
    const renderDayContent = useCallback((dayInfo) => {
        const { date: dayDate } = dayInfo;
        const dayBounds = getDayBounds(dayDate, workingHours);

        const dayEvents = filterEventsByDateRange(
            events,
            dayBounds.start || dayDate.startOf('day'),
            dayBounds.end || dayDate.endOf('day')
        ).filter(event => dayjs(event.startTime).isSame(dayDate, 'day'));

        const positionedEvents = calculateEventPositions(dayEvents);
        const nonWorkingBlocks = getNonWorkingBlocks(dayDate, workingHours, timeRange);

        if (!selectedTrainer) {
            return (
                <div className="day-content" style={{
                    height: `${gridDimensions.totalHeight}px`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                }}>
                    <div className="no-hours">Wybierz trenera</div>
                </div>
            );
        }

        if (workingHours.length === 0) {
            return (
                <div className="day-content" style={{
                    height: `${gridDimensions.totalHeight}px`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                }}>
                    <div className="no-hours">Ładowanie godzin pracy...</div>
                </div>
            );
        }

        const dayStartTime = dayDate.hour(parseInt(timeRange.earliestTime.split(':')[0])).minute(0).second(0);

        return (
            <div
                className="day-content"
                style={{
                    height: `${gridDimensions.totalHeight}px`,
                    position: 'relative',
                    cursor: isLockMode ? 'crosshair' : 'default',
                }}
                onMouseDown={(e) => {
                    const rect = e.currentTarget.getBoundingClientRect();
                    const clickY = e.clientY - rect.top;
                    const rawClickTime = pixelsToTime(clickY, dayStartTime, gridDimensions.hourHeight);
                    const snappedClickTime = snapToGrid(rawClickTime, 15);

                    const isInWorkingHours = dayBounds.isWorkingDay &&
                        dayBounds.start &&
                        dayBounds.end &&
                        snappedClickTime.isSameOrAfter(dayBounds.start) &&
                        snappedClickTime.isSameOrBefore(dayBounds.end);

                    if (isInWorkingHours) {
                        handleEmptySlotClick(e, dayDate);
                    }
                }}
            >
                {/* Render non-working hour blocks - NO BORDERS */}
                {nonWorkingBlocks.map((block, index) => {
                    const blockTop = timeToPixels(block.start, dayStartTime, gridDimensions.hourHeight);
                    const blockHeight = timeToPixels(block.end, dayStartTime, gridDimensions.hourHeight) - blockTop;

                    return (
                        <div
                            key={`non-working-${index}`}
                            className={`non-working-hours ${block.type}`}
                            style={{
                                position: 'absolute',
                                top: `${blockTop}px`,
                                left: 0,
                                right: 0,
                                height: `${blockHeight}px`,
                                background: 'rgba(0, 0, 0, 0.05)',
                                // REMOVED: all borders to prevent double lines
                                border: 'none',
                                borderTop: 'none',
                                borderBottom: 'none',
                                pointerEvents: 'none',
                                zIndex: 2,
                            }}
                        >
                            {block.type === 'no-work-day' && blockHeight > 100 && (
                                <div style={{
                                    position: 'absolute',
                                    top: '50%',
                                    left: '50%',
                                    transform: 'translate(-50%, -50%)',
                                    color: 'rgba(0, 0, 0, 0.3)',
                                    fontSize: '14px',
                                    fontWeight: '500',
                                }}>
                                    Dzień wolny
                                </div>
                            )}
                        </div>
                    );
                })}

                {/* If no working hours for this day, show message */}
                {!dayBounds.isWorkingDay && (
                    <div style={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        color: 'rgba(0, 0, 0, 0.4)',
                        fontSize: '16px',
                        fontWeight: '500',
                        textAlign: 'center',
                        pointerEvents: 'none',
                        zIndex: 3,
                    }}>
                        Brak godzin pracy
                    </div>
                )}

                {/* Render positioned events */}
                {positionedEvents.map((event, eventIndex) => {
                    // Find temp lock index if this is a temporary lock
                    let tempLockIndex = -1;
                    if (event.isTemporary && selectedLockSlots.length > 0) {
                        tempLockIndex = selectedLockSlots.findIndex(lock => {
                            return lock.startTime === event.startTime &&
                                lock.endTime === event.endTime;
                        });
                    }

                    return (
                        <TrainingEvent
                            key={event.id || `${event.startTime}-${event.endTime}`}
                            event={event}
                            dayStart={dayStartTime}
                            hourHeight={gridDimensions.hourHeight}
                            columnOffset={event.column}
                            totalColumns={event.totalColumns}
                            {...memoizedEventHandlers}
                            isDraggable={!isLockMode && event.confirmed}
                            isResizable={!isLockMode && event.confirmed}
                            onEditTemporaryLock={event.isTemporary && tempLockIndex >= 0 ?
                                () => onEditTemporaryLock(tempLockIndex, event) :
                                null
                            }
                            onDeleteTemporaryLock={event.isTemporary && tempLockIndex >= 0 ?
                                () => onDeleteTemporaryLock(tempLockIndex) :
                                null
                            }
                        />
                    );
                })}

                {/* Selection box for creating new events (normal mode only) */}
                {!isLockMode && selectionBox && dayDate.isSame(selectionBox.startTime, 'day') && (
                    <div
                        className="event-block event-selection"
                        style={{
                            position: 'absolute',
                            top: `${selectionBox.top}px`,
                            left: '10px',
                            right: '10px',
                            height: `${selectionBox.height}px`,
                            background: 'rgba(172, 166, 255, 0.3)',
                            border: '2px dashed #ACA6FF',
                            borderRadius: '12px',
                            pointerEvents: 'none',
                        }}
                    />
                )}
            </div>
        );
    }, [
        events,
        workingHours,
        selectedTrainer,
        gridDimensions.totalHeight,
        gridDimensions.hourHeight,
        isLockMode,
        handleEmptySlotClick,
        memoizedEventHandlers,
        selectionBox,
        timeRange,
        selectedLockSlots,
        onEditTemporaryLock,
        onDeleteTemporaryLock
    ]);

    // FIXED: Create consistent grid lines for ALL hours
    const gridLines = useMemo(() => {
        const lines = [];
        const [startHour] = timeRange.earliestTime.split(':').map(Number);
        const hourCount = Math.ceil(gridDimensions.totalHeight / gridDimensions.hourHeight);

        // Create lines for EVERY hour (not just some)
        for (let i = 1; i < hourCount; i++) {
            lines.push(
                <div
                    key={`grid-line-${i}`}
                    style={{
                        position: 'absolute',
                        top: `${82 + (i * gridDimensions.hourHeight)}px`,
                        left: '0',
                        right: '0',
                        height: '1px',
                        background: '#e5e7eb',
                        pointerEvents: 'none',
                        zIndex: 1,
                    }}
                />
            );
        }

        return lines;
    }, [gridDimensions.totalHeight, gridDimensions.hourHeight, timeRange.earliestTime]);

    return (
        <div className="calendar-grid" ref={containerRef}>
            <div
                className={`trainer-calendar-week ${isLockMode ? 'lock-mode-active' : ''}`}
                style={{
                    position: 'relative',
                }}
            >
                {/* Consistent horizontal grid lines for ALL hours */}
                {gridLines}

                {/* Time column */}
                <TimeGrid
                    timeRange={timeRange}
                    hourHeight={gridDimensions.hourHeight}
                />

                {/* Day columns */}
                {daysOfWeek.map((dayInfo) => {
                    const isToday = dayInfo.isToday;

                    return (
                        <div
                            key={dayInfo.date.toString()}
                            className={`calendar-day ${isToday ? 'today' : ''}`}
                        >
                            {/* Day header */}
                            <div className="calendar-day-header">
                                <div className="day-name">{dayNamesPl[dayInfo.date.isoWeekday() - 1]}</div>
                                <div className="day-date">{dayInfo.dayNumber}</div>
                            </div>

                            {/* Day content */}
                            {renderDayContent(dayInfo)}
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

export default React.memo(CalendarGrid);